import { create } from 'zustand';

/**
 * Theme selection (TZ section 14).
 *
 * Three choices, not two: an operator who keeps a dark desktop still sometimes
 * wants the panel light in a bright room, and "system" has to keep tracking the
 * operating system afterwards rather than freezing at whatever it was when the
 * page loaded. `useApplyTheme` (hooks/) puts the resolved theme on the page.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/** Shared with public/theme.js, which applies the theme before
 *  React mounts. Changing it here means changing it there. */
export const THEME_STORAGE_KEY = 'cp.theme';

export const DARK_QUERY = '(prefers-color-scheme: dark)';

function readStored(): ThemeChoice {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
  } catch {
    // Private browsing or a blocked storage partition. A theme is not worth
    // failing to render over.
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  return globalThis.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

interface ThemeState {
  readonly choice: ThemeChoice;
  /** What the operating system prefers right now. */
  readonly system: ResolvedTheme;
  setChoice(next: ThemeChoice): void;
  setSystem(next: ResolvedTheme): void;
}

export const useThemeStore = create<ThemeState>()((set) => ({
  choice: readStored(),
  system: systemTheme(),
  setChoice(next) {
    set({ choice: next });
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // The choice still applies for this session; it simply will not survive
      // a reload. Silently failing to switch would be the worse outcome.
    }
  },
  setSystem(next) {
    set({ system: next });
  },
}));

/** The theme the page is drawn in. */
export function resolvedTheme(state: Pick<ThemeState, 'choice' | 'system'>): ResolvedTheme {
  return state.choice === 'system' ? state.system : state.choice;
}
